import React, { Ref, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { AutoSizer } from 'react-virtualized';
import { ListOnItemsRenderedProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { DynamicList, DynamicListHandles, DynamicListProps } from '@tanium/react-dynamic-list';
import { useListOverflow } from '../../hooks/useListOverflow';
import { Id } from '../../types/Id';
import { RibbonListVirtualizedProps } from '../../types/StackedRibbonListComponentTypes';
import { ItemRenderer } from '../Item/ItemRenderer';
import { HEADER_HEIGHT } from '../ListHeader/RibbonListHeader';
import { EmptyState } from './EmptyState';
import { ListProps } from './ListProps';
import { RibbonListVirtualizedHandles } from './RibbonListHandles';

const overscanCount = 5;

interface Props<TId extends Id, TItem>
  extends ListProps<TId, TItem>,
    RibbonListVirtualizedProps<TId, TItem> {}

/**
 * A component that renders the main body of a StackedRibbonList using DOM virtualization. This
 * component should be rendered inside a RibbonListWrapper.
 */
const RibbonListVirtualizedInner = <TId extends Id, TItem>(
  {
    estimatedItemSize = 30,
    isItemLoaded,
    itemCount,
    itemIdGetter,
    loadMoreItems,
    minimumBatchSize = 25,
    onListOverflowChange,
  }: Props<TId, TItem>,
  ref: React.Ref<RibbonListVirtualizedHandles>,
) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const infiniteLoader = useRef<InfiniteLoader>(null);

  const innerRef = useListOverflow(outerRef, onListOverflowChange);

  const loadMoreItemsInner = useCallback(
    (startIndex: number, stopIndex: number) => {
      loadMoreItems?.(startIndex, stopIndex);
      return Promise.resolve();
    },
    [loadMoreItems],
  );

  useEffect(() => {
    // No way to set `role` on AutoSizer via props
    const autoSizerContainer = outerRef.current?.parentElement;
    // AutoSizer *should* have no role of its own, but just to be safe...
    if (autoSizerContainer && !autoSizerContainer.getAttribute('role')) {
      autoSizerContainer.setAttribute('role', 'rowgroup');
    }
  }, []);

  useImperativeHandle(ref, () => ({
    refreshList: () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      infiniteLoader.current!.resetloadMoreItemsCache();
    },
    scrollToTop: () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outerRef.current!.scrollTop = 0;
    },
  }));

  const infiniteLoaderChild = useCallback(
    ({
      onItemsRendered,
      ref: loaderRef,
    }: {
      onItemsRendered: (props: ListOnItemsRenderedProps) => unknown;
      ref: Ref<DynamicListHandles>;
    }) => (
      <AutoSizer>
        {({ height, width }) => (
          <DynamicList
            role="presentation"
            ref={loaderRef}
            height={height - HEADER_HEIGHT}
            width={width}
            estimatedItemSize={estimatedItemSize}
            itemCount={itemCount}
            overscanCount={overscanCount}
            itemKey={itemIdGetter}
            onItemsRendered={onItemsRendered}
            innerElementType={itemCount === 0 ? EmptyState : 'div'}
            outerRef={outerRef}
            innerRef={innerRef}
          >
            {ItemRenderer as DynamicListProps['children']}
          </DynamicList>
        )}
      </AutoSizer>
    ),
    [estimatedItemSize, innerRef, itemCount, itemIdGetter],
  );

  return (
    <InfiniteLoader
      ref={infiniteLoader}
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={loadMoreItemsInner}
      minimumBatchSize={minimumBatchSize}
    >
      {infiniteLoaderChild}
    </InfiniteLoader>
  );
};

export const RibbonListVirtualized = React.forwardRef(RibbonListVirtualizedInner);
